import type React from 'react';
import { Button, Drawer, Grid, Input, Select, Tooltip, TreeSelect } from 'antd';
import { FilterOutlined, SettingOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { ProductSaleType } from '@tcr-erp/shared';
import type { ProductSortKey } from '@tcr-erp/shared';
import { useCategoryOptions } from '@/hooks/useCategories';
import { useBrandList } from '@/hooks/useBrands';
import { useFilterableAttributes } from '@/hooks/useFilterableAttributes';
import { FacetedFilter } from '@/components/FacetedFilter/FacetedFilter';
import { PRODUCT_STATE_OPTIONS } from '@/pages/Products/parts/constants';
import { hasActiveFilter, type ProductFilterValue } from '@/pages/Products/parts/filter-state';

interface ProductToolbarProps {
  value: ProductFilterValue;
  onChange: (next: ProductFilterValue) => void;
  /** Đưa mọi filter về mặc định. */
  onReset: () => void;
  /** Mở ColumnSettingsDrawer của ERPTable. */
  onOpenSettings: () => void;
  /** Nhập dữ liệu / Xuất dữ liệu / Thêm sản phẩm — trang lắp vào vì chúng mang state và quyền riêng. */
  actions: React.ReactNode;
}

const saleTypeOptions = [
  { value: ProductSaleType.ONLINE, label: 'Online' },
  { value: ProductSaleType.STORE, label: 'Tại chi nhánh' },
];

const sortOptions = [
  { value: 'name_asc', label: 'Tên A→Z' },
  { value: 'name_desc', label: 'Tên Z→A' },
  { value: 'created_at_desc', label: 'Mới nhất' },
  { value: 'sold_count_desc', label: 'Bán chạy' },
];

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
};

const { useBreakpoint } = Grid;

export function ProductToolbar({ value, onChange, onReset, onOpenSettings, actions }: ProductToolbarProps) {
  const { treeData: categoryTreeData } = useCategoryOptions();
  const { data: brandData } = useBrandList({ page: 1, page_size: 200 });
  const { data: attributes } = useFilterableAttributes();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const mainFilters = (
    <>
      <Input.Search
        placeholder="Tìm theo tên / SKU"
        value={value.q}
        onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
        style={{ flex: '1 1 250px', minWidth: isMobile ? '100%' : 180 }}
        allowClear
      />
      <TreeSelect<string[]>
        multiple
        showSearch
        treeNodeFilterProp="title"
        treeDefaultExpandAll
        placeholder="Danh mục"
        value={value.category_id}
        onChange={(v) => onChange({ ...value, category_id: v?.length ? v : undefined })}
        treeData={categoryTreeData}
        style={{ flex: '1 1 177px', minWidth: isMobile ? '100%' : 140 }}
        popupMatchSelectWidth={false}
        maxTagCount="responsive"
        allowClear
      />
      <Select<string[]>
        mode="multiple"
        placeholder="Thương hiệu"
        value={value.brand_id}
        onChange={(v) => onChange({ ...value, brand_id: v.length ? v : undefined })}
        options={(brandData?.data ?? []).map((b) => ({ value: b._id, label: b.name }))}
        style={{ flex: '1 1 166px', minWidth: isMobile ? '100%' : 130 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select<number[]>
        mode="multiple"
        placeholder="Trạng thái"
        value={value.state}
        onChange={(v) => onChange({ ...value, state: v.length ? v : undefined })}
        options={PRODUCT_STATE_OPTIONS}
        style={{ flex: '1 1 195px', minWidth: isMobile ? '100%' : 155 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select<string[]>
        mode="multiple"
        placeholder="Kênh bán"
        value={value.sale_type}
        onChange={(v) => onChange({ ...value, sale_type: v.length ? v : undefined })}
        options={saleTypeOptions}
        style={{ flex: '1 1 128px', minWidth: isMobile ? '100%' : 100 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select
        placeholder="Sắp xếp"
        value={value.sort}
        onChange={(v: ProductSortKey | undefined) => onChange({ ...value, sort: v })}
        options={sortOptions}
        style={{ flex: '1 1 115px', minWidth: isMobile ? '100%' : 100 }}
        allowClear
      />
    </>
  );

  const secondaryFilters = (
    <>
      {hasActiveFilter(value) && (
        <Button danger ghost onClick={onReset} style={isMobile ? { width: '100%' } : undefined}>
          Xoá tất cả
        </Button>
      )}
      {(attributes ?? []).map((attr) => (
        <FacetedFilter
          key={attr.code}
          title={attr.name}
          options={attr.values}
          value={(value.attrs?.[attr.code] ?? []) as string[]}
          onChange={(selected) => {
            const newAttrs = { ...(value.attrs ?? {}) };
            if (selected.length > 0) {
              newAttrs[attr.code] = selected;
            } else {
              delete newAttrs[attr.code];
            }
            onChange({
              ...value,
              attrs: Object.keys(newAttrs).length > 0 ? newAttrs : undefined,
            });
          }}
        />
      ))}
    </>
  );

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Input.Search
          placeholder="Tìm theo tên / SKU"
          value={value.q}
          onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
          allowClear
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
            Bộ lọc
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {actions}
            <Tooltip title="Tuỳ chỉnh cột">
              <Button aria-label="Tuỳ chỉnh cột" icon={<SettingOutlined />} onClick={onOpenSettings} />
            </Tooltip>
          </div>
        </div>
        <Drawer
          title="Bộ lọc sản phẩm"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          placement="bottom"
          height="85vh"
          styles={{ body: { paddingTop: 12 } }}
          extra={
            <Button type="primary" onClick={() => setFilterDrawerOpen(false)}>
              Áp dụng
            </Button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mainFilters}
            {secondaryFilters}
          </div>
        </Drawer>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Hàng 1: filter chính + hành động */}
      <div style={{ ...rowStyle, flexWrap: 'wrap' }}>
        {mainFilters}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
          {actions}
          <Tooltip title="Tuỳ chỉnh cột">
            <Button aria-label="Tuỳ chỉnh cột" icon={<SettingOutlined />} onClick={onOpenSettings} />
          </Tooltip>
        </div>
      </div>

      {/* Hàng 2: xoá tất cả + filter thuộc tính */}
      <div style={rowStyle}>
        {secondaryFilters}
      </div>
    </div>
  );
}
